import React from "react";
import {autobind} from "core-decorators";
import {Button, Input, Popover} from "antd";
import {FaPen, FaRegFileAlt} from "react-icons/fa";
import {action, observable} from "mobx";
import {inject, observer} from "mobx-react";
import {gql} from "@apollo/client";
import debounce from "lodash/debounce";

import {ChangeRehearsalComment, RehearsalsQuery} from "@/graphql/types";
import Store from "@/stores";

export interface IRehearsalsNotesProps {
  rehearsal: RehearsalsQuery["rehearsals"][0];
  store?: Store;
}

function noteIsEmpty(note?: string | null): boolean {
  return note === null || note === undefined || note.trim() === "";
}

@inject("store")
@autobind
@observer
export default class RehearsalsNotes extends React.Component<IRehearsalsNotesProps> {
  @observable note?: string | null = null;
  changeCommentDebounced = debounce(this.changeComment, 1000);

  componentDidMount() {
    this.setNote(this.props.rehearsal.comment);
  }

  async changeComment(comment: string | null) {
    if (noteIsEmpty(comment)) comment = null;
    const {rehearsal} = this.props;
    const previousComment = rehearsal.comment;
    if (previousComment === comment) return;
    try {
      const result = await this.props.store!.apollo.client.mutate<ChangeRehearsalComment>({
        mutation: gql`
          mutation ChangeRehearsalComment($id: Int!, $comment: String) {
            rehearsal: update_rehearsals_by_pk(pk_columns: {id: $id}, _set: {comment: $comment}) {
              id
              comment
            }
          }
        `,
        variables: {
          id: rehearsal.id,
          comment,
        },
      });
      const updatedRehearsal = result.data?.rehearsal;
      if (!updatedRehearsal) throw new Error("Could not update Rehearsal's comment");
      this.setComment(updatedRehearsal.comment);
    } catch (error) {
      this.setComment(previousComment);
      this.setNote(previousComment); // delete unsaved text on error
      console.error(error);
    }
  }

  onChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    const note = event.target.value;
    this.setNote(note);
    this.changeCommentDebounced(note);
  }

  @action
  setComment(comment?: string | null) {
    const {rehearsal} = this.props;
    rehearsal.comment = comment;
  }

  @action
  setNote(note?: string | null) {
    this.note = note;
  }

  public render() {
    return (
      <div style={{display: "flex", justifyContent: "center", alignItems: "center"}}>
        <Popover
          title="My notes"
          content={
            <Input.TextArea
              autoSize={{minRows: 2, maxRows: 6}}
              value={this.note || undefined}
              onChange={this.onChange}
            />
          }
          trigger="click"
          arrowPointAtCenter
        >
          <Button type="link" shape="circle" size="small">
            {noteIsEmpty(this.note) ? <FaPen /> : <FaRegFileAlt />}
          </Button>
        </Popover>
      </div>
    );
  }
}
